import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useCases } from '../../../core/di/useCases';

/**
 * Hook pour vérifier les relations d'abonnement entre utilisateurs
 * @param {string} targetUserId - L'ID de l'utilisateur cible
 * @returns {Object} - Informations sur l'état d'abonnement
 */
export const useFollowing = (targetUserId) => {
  const { user: currentUser, refreshUser } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Rafraîchir l'utilisateur connecté
  const refreshCurrentUser = useCallback(async () => {
    if (refreshUser) await refreshUser();
  }, [refreshUser]);

  // Met à jour l'état local à partir du currentUser
  const updateStatus = useCallback(() => {
    if (!currentUser?.uid || !targetUserId) {
      setIsFollowing(false);
      setIsPending(false);
      setIsLoading(false);
      return;
    }
    setIsFollowing((currentUser?.following || []).includes(targetUserId));
    setIsPending((currentUser?.pendingFollowRequests || []).includes(targetUserId));
    setIsLoading(false);
  }, [currentUser, targetUserId]);

  useEffect(() => {
    updateStatus();
  }, [currentUser, targetUserId, updateStatus]);

  // Suivre un utilisateur
  const followUser = useCallback(async () => {
    if (!currentUser?.uid || !targetUserId) return false;
    setIsLoading(true);
    try {
      await useCases.sendFollowRequest.execute(currentUser.uid, targetUserId);
      setIsPending(true); // Optimiste
      await refreshCurrentUser();
      updateStatus();
      setIsLoading(false);
      return true;
    } catch (err) {
      setError(err);
      setIsLoading(false);
      return false;
    }
  }, [currentUser, targetUserId, refreshCurrentUser, updateStatus]);

  // Se désabonner
  const unfollowUser = useCallback(async () => {
    if (!currentUser?.uid || !targetUserId) return false;
    setIsLoading(true);
    try {
      await useCases.unfollowUser.execute(currentUser.uid, targetUserId);
      setIsFollowing(false); // Optimiste
      setIsPending(false);
      await refreshCurrentUser();
      updateStatus();
      setIsLoading(false);
      return true;
    } catch (err) {
      setError(err);
      setIsLoading(false);
      return false;
    }
  }, [currentUser, targetUserId, refreshCurrentUser, updateStatus]);

  // Annuler une demande
  const cancelRequest = useCallback(async () => {
    if (!currentUser?.uid || !targetUserId) return false;
    setIsLoading(true);
    try {
      await useCases.cancelFollowRequest.execute(currentUser.uid, targetUserId);
      setIsPending(false); // Optimiste
      await refreshCurrentUser();
      updateStatus();
      setIsLoading(false);
      return true;
    } catch (err) {
      setError(err);
      setIsLoading(false);
      return false;
    }
  }, [currentUser, targetUserId, refreshCurrentUser, updateStatus]);

  return {
    isFollowing,
    isPending,
    isLoading,
    error,
    followUser,
    unfollowUser,
    cancelRequest,
    refreshCurrentUser
  };
}; 